import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'
import { friendlyError } from '../lib/permissions'
import NoAccess from './NoAccess'

/*
  Run this SQL in Supabase before first use:

  CREATE TABLE IF NOT EXISTS feedback (
    id              uuid DEFAULT gen_random_uuid() PRIMARY KEY,
    patient_id      uuid REFERENCES patients(id) ON DELETE SET NULL,
    patient_name    text NOT NULL,
    visit_date      date,
    doctor_rating   integer CHECK (doctor_rating BETWEEN 1 AND 5),
    staff_rating    integer CHECK (staff_rating BETWEEN 1 AND 5),
    cleanliness_rating integer CHECK (cleanliness_rating BETWEEN 1 AND 5),
    wait_time_rating   integer CHECK (wait_time_rating BETWEEN 1 AND 5),
    overall_rating  integer CHECK (overall_rating BETWEEN 1 AND 5),
    comment         text,
    created_at      timestamptz DEFAULT now()
  );
  ALTER TABLE feedback ENABLE ROW LEVEL SECURITY;
*/

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  teal950: '#082828', teal900: '#0d3d3d', teal800: '#0f5454',
  teal700: '#126868', teal600: '#148080', teal500: '#19a8a8',
  teal400: '#2ecece', teal200: '#90e0e0', teal100: '#ccf2f2', teal50: '#edfafa',
  white: '#ffffff',
  gray50: '#f8fafc', gray100: '#f1f5f9', gray200: '#e2e8f0',
  gray300: '#cbd5e1', gray400: '#94a3b8', gray500: '#64748b',
  gray600: '#475569', gray700: '#334155', gray900: '#0f172a',
  red50: '#fff1f2', red500: '#ef4444', red600: '#dc2626',
  green50: '#f0fdf4', green600: '#16a34a',
  amber50: '#fffbeb', amber400: '#fbbf24', amber500: '#f59e0b', amber600: '#d97706',
  orange50: '#fff7ed', orange100: '#ffedd5', orange600: '#ea580c', orange700: '#c2410c',
  font: "'Segoe UI', system-ui, -apple-system, sans-serif",
}

const RATING_CATEGORIES = [
  { key: 'doctor_rating',       label: 'Doctor',      icon: '👨‍⚕️' },
  { key: 'staff_rating',        label: 'Staff',       icon: '👥' },
  { key: 'cleanliness_rating',  label: 'Cleanliness', icon: '✨' },
  { key: 'wait_time_rating',    label: 'Wait Time',   icon: '⏱️' },
  { key: 'overall_rating',      label: 'Overall',     icon: '⭐' },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────
function avg(list, key) {
  const vals = list.map(r => Number(r[key])).filter(v => v > 0)
  if (!vals.length) return 0
  return vals.reduce((a, b) => a + b, 0) / vals.length
}

function Stars({ value, size = 16, interactive = false, onChange }) {
  const [hover, setHover] = useState(0)
  const display = interactive ? (hover || value) : value
  return (
    <span style={{ display: 'inline-flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map(n => (
        <span
          key={n}
          onClick={interactive ? () => onChange(n) : undefined}
          onMouseEnter={interactive ? () => setHover(n) : undefined}
          onMouseLeave={interactive ? () => setHover(0) : undefined}
          style={{
            fontSize: size,
            cursor: interactive ? 'pointer' : 'default',
            color: n <= display ? T.amber400 : T.gray200,
            transition: 'color 0.1s',
            userSelect: 'none',
          }}
        >★</span>
      ))}
    </span>
  )
}

function RatingBar({ label, icon, value }) {
  const pct = (value / 5) * 100
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span style={{ width: 90, fontSize: 12.5, fontWeight: 600, color: T.gray600 }}>{label}</span>
      <div style={{
        flex: 1, height: 8, borderRadius: 4,
        background: T.gray200, overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: pct >= 80 ? T.teal500 : pct >= 60 ? T.amber400 : T.orange600,
          borderRadius: 4, transition: 'width 0.4s',
        }} />
      </div>
      <span style={{
        width: 30, fontSize: 13, fontWeight: 700,
        color: value >= 4 ? T.teal700 : value >= 3 ? T.amber600 : T.orange600,
        textAlign: 'right',
      }}>
        {value > 0 ? value.toFixed(1) : '—'}
      </span>
    </div>
  )
}

// ─── Add Feedback Modal ────────────────────────────────────────────────────────
function FeedbackModal({ onClose, onSaved }) {
  const { userProfile } = useAuth()
  const EMPTY = {
    patient_name: '', visit_date: new Date().toISOString().split('T')[0],
    doctor_rating: 0, staff_rating: 0, cleanliness_rating: 0,
    wait_time_rating: 0, overall_rating: 0, comment: '',
  }
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [patientSearch, setPatientSearch] = useState('')
  const [patientResults, setPatientResults] = useState([])

  async function searchPatients(q) {
    if (!q || q.length < 2) { setPatientResults([]); return }
    const { data } = await supabase
      .from('patients')
      .select('id, full_name')
      .ilike('full_name', `%${q}%`)
      .limit(5)
    setPatientResults(data || [])
  }

  useEffect(() => {
    const t = setTimeout(() => searchPatients(patientSearch), 300)
    return () => clearTimeout(t)
  }, [patientSearch])

  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }

  async function save() {
    if (!form.patient_name.trim()) { setError('Patient name is required'); return }
    if (RATING_CATEGORIES.every(c => !form[c.key])) { setError('Please provide at least one rating'); return }
    setSaving(true); setError('')
    const { error: err } = await supabase.from('feedback').insert({
      patient_name: form.patient_name.trim(),
      visit_date: form.visit_date || null,
      doctor_rating: form.doctor_rating || null,
      staff_rating: form.staff_rating || null,
      cleanliness_rating: form.cleanliness_rating || null,
      wait_time_rating: form.wait_time_rating || null,
      overall_rating: form.overall_rating || null,
      comment: form.comment.trim() || null,
      clinic_id: userProfile?.clinic_id,
    })
    if (err) { setError(friendlyError(err)); setSaving(false); return }
    setSaving(false)
    onSaved()
    onClose()
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(0,0,0,0.45)', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      fontFamily: T.font, padding: 16,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: T.white, borderRadius: 16, width: '100%', maxWidth: 520,
        boxShadow: '0 24px 64px rgba(0,0,0,0.22)', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${T.teal800} 0%, ${T.teal600} 100%)`,
          padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: T.white }}>Add Patient Feedback</div>
            <div style={{ fontSize: 12, color: T.teal100, marginTop: 2 }}>Record a patient's experience</div>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8,
            width: 32, height: 32, color: T.white, fontSize: 18, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>×</button>
        </div>

        <div style={{ padding: '24px', maxHeight: '70vh', overflowY: 'auto' }}>
          {error && (
            <div style={{
              background: T.red50, border: `1px solid #fecdd3`, borderRadius: 8,
              padding: '10px 14px', color: T.red600, fontSize: 13, marginBottom: 16,
            }}>⚠️ {error}</div>
          )}

          {/* Patient name */}
          <div style={{ marginBottom: 16, position: 'relative' }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: T.gray600, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Patient Name *
            </label>
            <input
              value={patientSearch}
              onChange={e => { setPatientSearch(e.target.value); set('patient_name', e.target.value) }}
              placeholder="Type patient name…"
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 13,
                border: `1.5px solid ${T.gray200}`, outline: 'none', fontFamily: T.font,
                boxSizing: 'border-box',
              }}
            />
            {patientResults.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                background: T.white, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                border: `1px solid ${T.gray200}`, overflow: 'hidden',
              }}>
                {patientResults.map(p => (
                  <div key={p.id} onClick={() => { setPatientSearch(p.full_name); set('patient_name', p.full_name); setPatientResults([]) }}
                    style={{
                      padding: '10px 14px', cursor: 'pointer', fontSize: 13,
                      borderBottom: `1px solid ${T.gray100}`,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = T.teal50}
                    onMouseLeave={e => e.currentTarget.style.background = T.white}
                  >
                    👤 {p.full_name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Visit date */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: T.gray600, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Visit Date
            </label>
            <input
              type="date" value={form.visit_date}
              onChange={e => set('visit_date', e.target.value)}
              style={{
                padding: '10px 12px', borderRadius: 8, fontSize: 13,
                border: `1.5px solid ${T.gray200}`, outline: 'none', fontFamily: T.font,
              }}
            />
          </div>

          {/* Ratings */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.gray600, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Ratings
            </div>
            {RATING_CATEGORIES.map(cat => (
              <div key={cat.key} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <span style={{ fontSize: 16 }}>{cat.icon}</span>
                <span style={{ width: 90, fontSize: 13, fontWeight: 600, color: T.gray700 }}>{cat.label}</span>
                <Stars value={form[cat.key]} size={22} interactive onChange={v => set(cat.key, v)} />
                {form[cat.key] > 0 && (
                  <span style={{ fontSize: 12, color: T.gray400 }}>{form[cat.key]}/5</span>
                )}
              </div>
            ))}
          </div>

          {/* Comment */}
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: T.gray600, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Comment
            </label>
            <textarea
              value={form.comment}
              onChange={e => set('comment', e.target.value)}
              placeholder="Patient's comments or suggestions…"
              rows={3}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 13,
                border: `1.5px solid ${T.gray200}`, outline: 'none', fontFamily: T.font,
                resize: 'vertical', boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: `1px solid ${T.gray100}`,
          display: 'flex', justifyContent: 'flex-end', gap: 10,
        }}>
          <button onClick={onClose} style={{
            padding: '9px 18px', borderRadius: 8, border: `1.5px solid ${T.gray200}`,
            background: T.white, color: T.gray600, fontSize: 13, fontWeight: 600,
            cursor: 'pointer', fontFamily: T.font,
          }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{
            padding: '9px 22px', borderRadius: 8, border: 'none',
            background: `linear-gradient(135deg, ${T.teal700} 0%, ${T.teal500} 100%)`,
            color: T.white, fontSize: 13, fontWeight: 700,
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
            fontFamily: T.font,
          }}>
            {saving ? '⏳ Saving…' : '💾 Save Feedback'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Feedback card ─────────────────────────────────────────────────────────────
function FeedbackCard({ item }) {
  const overall = item.overall_rating || 0
  const isNegative = overall > 0 && overall <= 3
  const initials = (item.patient_name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

  return (
    <div style={{
      background: isNegative ? T.orange50 : T.white,
      border: `1.5px solid ${isNegative ? T.orange100 : T.gray200}`,
      borderLeft: `4px solid ${isNegative ? T.orange600 : T.teal500}`,
      borderRadius: 12, padding: '16px 20px', marginBottom: 12,
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        {/* Avatar */}
        <div style={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
          background: isNegative ? `${T.orange600}22` : `${T.teal600}22`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 700,
          color: isNegative ? T.orange700 : T.teal700,
        }}>{initials}</div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Top row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.gray900 }}>
                {item.patient_name}
              </span>
              {isNegative && (
                <span style={{
                  marginLeft: 8, fontSize: 10.5, fontWeight: 700,
                  background: T.orange100, color: T.orange700,
                  borderRadius: 10, padding: '2px 8px',
                }}>⚠️ Needs Attention</span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {overall > 0 && <Stars value={overall} size={15} />}
              <span style={{ fontSize: 11.5, color: T.gray400 }}>
                {item.visit_date
                  ? new Date(item.visit_date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                  : new Date(item.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                }
              </span>
            </div>
          </div>

          {/* Mini ratings */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 8 }}>
            {RATING_CATEGORIES.filter(c => c.key !== 'overall_rating' && item[c.key]).map(cat => (
              <span key={cat.key} style={{ fontSize: 11.5, color: T.gray500 }}>
                {cat.icon} {cat.label}: <strong style={{ color: T.gray700 }}>{item[cat.key]}/5</strong>
              </span>
            ))}
          </div>

          {/* Comment */}
          {item.comment && (
            <div style={{
              marginTop: 10, fontSize: 13, color: T.gray700,
              fontStyle: 'italic', lineHeight: 1.5,
              borderLeft: `3px solid ${isNegative ? T.orange600 : T.teal400}`,
              paddingLeft: 10,
            }}>
              "{item.comment}"
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Main Feedback page ────────────────────────────────────────────────────────
export default function Feedback() {
  const { isAdmin, userProfile } = useAuth()
  const [savedMsg, setSavedMsg] = useState('')
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState('')
  const [filter, setFilter]   = useState('all')   // all | 5 | 4 | low
  const [showModal, setShowModal] = useState(false)

  const load = useCallback(async () => {
    // Feedback results are admin-only; everyone else can still record feedback
    if (!isAdmin) { setLoading(false); return }
    setLoading(true); setError('')
    const { data, error: err } = await supabase
      .from('feedback')
      .select('*')
      .order('created_at', { ascending: false })
    if (err) { setError(friendlyError(err)) }
    else { setRecords(data || []) }
    setLoading(false)
  }, [isAdmin])

  useEffect(() => { load() }, [load])

  const filtered = records.filter(r => {
    if (filter === '5')   return r.overall_rating === 5
    if (filter === '4')   return r.overall_rating === 4
    if (filter === 'low') return r.overall_rating > 0 && r.overall_rating <= 3
    return true
  })

  // Aggregate stats
  const avgOverall = avg(records, 'overall_rating')
  const filledRatings = records.filter(r => r.overall_rating > 0)
  const dist = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: filledRatings.filter(r => r.overall_rating === star).length,
    pct: filledRatings.length ? (filledRatings.filter(r => r.overall_rating === star).length / filledRatings.length) * 100 : 0,
  }))
  const negativeCount = records.filter(r => r.overall_rating > 0 && r.overall_rating <= 3).length

  const FILTERS = [
    { id: 'all', label: `All (${records.length})` },
    { id: '5',   label: '★★★★★ 5 Star' },
    { id: '4',   label: '★★★★☆ 4 Star' },
    { id: 'low', label: '⚠️ 3 Star & Below' },
  ]

  return (
    <div style={{ flex: 1, overflowY: 'auto', fontFamily: T.font, background: T.gray50 }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 32px' }}>

        {/* Page header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: T.gray900, margin: 0 }}>
              💬 Patient Feedback
            </h1>
            <p style={{ fontSize: 13, color: T.gray400, margin: '4px 0 0' }}>
              Track patient satisfaction and clinic performance
            </p>
          </div>
          <button onClick={() => setShowModal(true)} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', borderRadius: 10, border: 'none',
            background: `linear-gradient(135deg, ${T.teal700} 0%, ${T.teal500} 100%)`,
            color: T.white, fontSize: 13.5, fontWeight: 700,
            cursor: 'pointer', fontFamily: T.font,
            boxShadow: `0 4px 14px ${T.teal700}44`,
          }}>
            ＋ Add Feedback
          </button>
        </div>

        {error && (
          <div style={{
            background: T.red50, border: `1px solid #fecdd3`, borderRadius: 10,
            padding: '12px 16px', color: T.red600, fontSize: 13, marginBottom: 20,
          }}>⚠️ {error}</div>
        )}

        {savedMsg && (
          <div style={{
            background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10,
            padding: '12px 16px', color: '#16a34a', fontSize: 13, fontWeight: 600, marginBottom: 20,
          }}>✅ {savedMsg}</div>
        )}

        {userProfile && !isAdmin && (
          <div style={{ background: T.white, borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
            <NoAccess
              message="Feedback results are visible to admins only."
              detail="You can still record a patient's feedback using “Add Feedback” above."
            />
          </div>
        )}

        {/* Stats row */}
        {isAdmin && !loading && records.length > 0 && (
          <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>

            {/* Overall score card */}
            <div style={{
              background: T.white, borderRadius: 14, padding: '22px 28px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
              borderLeft: `4px solid ${T.teal500}`,
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6,
              minWidth: 200,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.gray500, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Overall Rating
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontSize: 38, fontWeight: 800, color: T.gray900, lineHeight: 1 }}>
                  {avgOverall > 0 ? avgOverall.toFixed(1) : '—'}
                </span>
                <span style={{ fontSize: 14, color: T.gray400 }}>/ 5</span>
              </div>
              {avgOverall > 0 && <Stars value={Math.round(avgOverall)} size={18} />}
              <div style={{ fontSize: 12, color: T.gray400 }}>{filledRatings.length} ratings</div>
            </div>

            {/* Rating breakdown */}
            <div style={{
              background: T.white, borderRadius: 14, padding: '22px 28px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)', flex: 1, minWidth: 280,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.gray500, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 14 }}>
                Category Breakdown
              </div>
              {RATING_CATEGORIES.map(cat => (
                <RatingBar key={cat.key} label={cat.label} icon={cat.icon} value={avg(records, cat.key)} />
              ))}
            </div>

            {/* Star distribution */}
            <div style={{
              background: T.white, borderRadius: 14, padding: '22px 28px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)', minWidth: 220,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.gray500, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 14 }}>
                Distribution
              </div>
              {dist.map(({ star, count, pct }) => (
                <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: T.amber500, width: 18, textAlign: 'right', fontWeight: 700 }}>{star}★</span>
                  <div style={{ flex: 1, height: 7, background: T.gray100, borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{
                      width: `${pct}%`, height: '100%',
                      background: star >= 4 ? T.teal500 : star === 3 ? T.amber400 : T.orange600,
                      borderRadius: 4,
                    }} />
                  </div>
                  <span style={{ fontSize: 11.5, color: T.gray400, width: 22, textAlign: 'right' }}>{count}</span>
                </div>
              ))}
              {negativeCount > 0 && (
                <div style={{
                  marginTop: 14, padding: '8px 12px', borderRadius: 8,
                  background: T.orange50, border: `1px solid ${T.orange100}`,
                  fontSize: 12, color: T.orange700, fontWeight: 600,
                }}>
                  ⚠️ {negativeCount} negative feedback{negativeCount !== 1 ? 's' : ''} need attention
                </div>
              )}
            </div>
          </div>
        )}

        {/* Filter tabs */}
        {isAdmin && <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              style={{
                padding: '7px 16px', borderRadius: 20, fontSize: 12.5, fontWeight: 600,
                cursor: 'pointer', fontFamily: T.font, transition: 'all 0.15s',
                border: filter === f.id ? `1.5px solid ${f.id === 'low' ? T.orange600 : T.teal600}` : `1.5px solid ${T.gray200}`,
                background: filter === f.id
                  ? f.id === 'low' ? T.orange50 : T.teal50
                  : T.white,
                color: filter === f.id
                  ? f.id === 'low' ? T.orange700 : T.teal700
                  : T.gray600,
              }}
            >{f.label}</button>
          ))}
        </div>}

        {/* Feedback list */}
        {!isAdmin ? null : loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: T.gray400, fontSize: 14 }}>
            Loading feedback…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '80px 0',
            background: T.white, borderRadius: 14,
            boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
          }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>💬</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.gray600 }}>
              {filter === 'all' ? 'No feedback yet' : 'No feedback in this category'}
            </div>
            <div style={{ fontSize: 13, color: T.gray400, marginTop: 6 }}>
              {filter === 'all' ? 'Add your first patient feedback using the button above' : 'Try a different filter'}
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 12, color: T.gray400, marginBottom: 12 }}>
              Showing {filtered.length} feedback{filtered.length !== 1 ? 's' : ''}
            </div>
            {filtered.map(item => (
              <FeedbackCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <FeedbackModal
          onClose={() => setShowModal(false)}
          onSaved={() => { setSavedMsg('Feedback recorded. Thank you!'); setTimeout(() => setSavedMsg(''), 3000); load() }}
        />
      )}
    </div>
  )
}
